import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, Client, PulseClient } from '../lib/api';

const STATUS_LABELS: Record<string, string> = {
  live_client: 'Live Client',
  signed_client: 'Signed Client',
  prospect_engaged: 'Prospect (Engaged)',
  prospect_early: 'Prospect (Early)',
  past: 'Past',
};

const ARCHETYPE_LABELS: Record<string, string> = {
  us_ria: 'US RIA',
  european_wealth_manager: 'European WM',
  family_office: 'Family Office',
  bank: 'Bank',
  etf_sponsor: 'ETF Sponsor',
  advisor_platform: 'Advisor Platform',
  other: 'Other',
};

function getStatusClass(status: string): string {
  switch (status) {
    case 'live_client':       return 'tag-valid';
    case 'signed_client':     return 'tag-treated';
    case 'prospect_engaged':  return 'tag-processing';
    case 'prospect_early':    return 'tag-unreviewed';
    case 'past':              return 'tag-unclear';
    default:                  return '';
  }
}

function TrendGlyph({ trend }: { trend: 'improving' | 'stable' | 'degrading' | undefined }) {
  if (!trend) return null;
  if (trend === 'improving') return <span className="trend-improving" title="Improving">&#9650;</span>;
  if (trend === 'degrading') return <span className="trend-degrading" title="Degrading">&#9660;</span>;
  return <span className="trend-stable" title="Stable">&#8211;</span>;
}

// A row needs attention if trend is degrading and not acked, OR there are overdue commitments.
function needsAttention(pulse: PulseClient | undefined, overdueIds: Set<string>, clientId: string): boolean {
  if (!pulse) return false;
  if (pulse.trend === 'degrading' && !pulse.acked) return true;
  if (overdueIds.has(clientId)) return true;
  return false;
}

export function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [pulseMap, setPulseMap] = useState<Record<string, PulseClient>>({});
  const [overdueClientIds, setOverdueClientIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientList, pulseData] = await Promise.all([
        api.listClients(),
        api.getPulseRelationships(),
      ]);

      setClients(clientList);

      // Build client_id → pulse row map
      const map: Record<string, PulseClient> = {};
      for (const p of pulseData.clients) {
        map[p.client_id] = p;
      }
      setPulseMap(map);

      // Build set of client_ids that appear in overdue_cap8 commitments
      const overdueIds = new Set<string>(
        pulseData.commitment_attention.overdue_cap8.map(c => c.client_id)
      );
      setOverdueClientIds(overdueIds);
    } catch {
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  // Sort: attention rows first, then by name
  const sortedClients = [...clients].sort((a, b) => {
    const aAttention = needsAttention(pulseMap[a.client_id], overdueClientIds, a.client_id) ? 0 : 1;
    const bAttention = needsAttention(pulseMap[b.client_id], overdueClientIds, b.client_id) ? 0 : 1;
    if (aAttention !== bAttention) return aAttention - bAttention;
    return a.name.localeCompare(b.name);
  });

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Clients</h1>
        <p className="page-subtitle">Manage client relationships and commitments</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Archetype</th>
              <th>Status</th>
              <th>Trend</th>
              <th>Open Commitments</th>
            </tr>
          </thead>
          <tbody>
            {sortedClients.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-muted" style={{ textAlign: 'center' }}>
                  No clients found
                </td>
              </tr>
            ) : (
              sortedClients.map((client) => {
                const pulse = pulseMap[client.client_id];
                const attention = needsAttention(pulse, overdueClientIds, client.client_id);
                return (
                  <tr
                    key={client.client_id}
                    onClick={() => navigate(`/clients/${client.client_id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {attention && <span className="attention-dot" />}
                        <strong style={{ color: 'var(--text-primary)' }}>{client.name}</strong>
                      </div>
                    </td>
                    <td>{ARCHETYPE_LABELS[client.archetype] || client.archetype}</td>
                    <td>
                      <span className={`tag ${getStatusClass(client.status)}`}>
                        {STATUS_LABELS[client.status] || client.status}
                      </span>
                    </td>
                    <td>
                      <TrendGlyph trend={pulse?.trend} />
                    </td>
                    <td>
                      {pulse && pulse.open_commitments > 0 ? (
                        <span className="tag tag-warning">{pulse.open_commitments} open</span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
